import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Topbar from "../../components/common/Topbar";
import Toast from "../../components/common/Toast";
import { showToast } from "../../store/uiSlice";
import { fmtUSD } from "../../utils/formatters";
import api from "../../services/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const Statement = () => {
  const dispatch = useDispatch();
  const { balance } = useSelector((state) => state.trade);
  const { user } = useSelector((state) => state.auth);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalPnl: 0,
    totalTransfers: 0,
  });
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    fetchTransactions();
  }, []);
  useEffect(() => {
    applyFilters();
  }, [fromDate, toDate, typeFilter, searchQuery, transactions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/statement");
      const data = response.data.transactions || response.data;
      setTransactions(data);
      calculateStats(data);
    } catch (error) {
      const demoTransactions = generateDemoTransactions();
      setTransactions(demoTransactions);
      calculateStats(demoTransactions);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      totalDeposits: data
        .filter((t) => t.type === "deposit")
        .reduce((s, t) => s + Math.abs(t.amount), 0),
      totalWithdrawals: data
        .filter((t) => t.type === "withdrawal")
        .reduce((s, t) => s + Math.abs(t.amount), 0),
      totalPnl: data
        .filter((t) => t.type === "trade")
        .reduce((s, t) => s + (t.pnl || t.amount || 0), 0),
      totalTransfers: data
        .filter((t) => t.type === "transfer")
        .reduce((s, t) => s + Math.abs(t.amount), 0),
    });
  };

  const applyFilters = () => {
    let filtered = [...transactions];
    if (fromDate)
      filtered = filtered.filter(
        (t) => (t.date || "").split("T")[0] >= fromDate,
      );
    if (toDate)
      filtered = filtered.filter((t) => (t.date || "").split("T")[0] <= toDate);
    if (typeFilter !== "all")
      filtered = filtered.filter((t) => t.type === typeFilter);
    if (searchQuery) {
      filtered = filtered.filter(
        (t) =>
          (t.description || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (t.type || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (t.instrumentId || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
      );
    }
    setFilteredTransactions(filtered);
  };

  // Export CSV
  const exportCSV = () => {
    const headers = ["Date", "Type", "Description", "Amount", "Balance"];
    const csvRows = [headers];
    filteredTransactions.forEach((t) => {
      csvRows.push([
        t.date || "",
        t.type || "",
        t.description || "",
        t.type === "deposit"
          ? `+${t.amount}`
          : t.type === "withdrawal"
            ? `-${Math.abs(t.amount)}`
            : t.pnl || t.amount || 0,
        t.balance || "",
      ]);
    });
    const csvContent = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `statement_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
    dispatch(
      showToast({ message: "CSV exported successfully!", type: "success" }),
    );
  };

  // Export Excel
  const exportExcel = () => {
    const excelData = filteredTransactions.map((t, idx) => ({
      "S.No": idx + 1,
      Date: t.date || "",
      Type: (t.type || "").toUpperCase(),
      Description: t.description || "",
      Amount:
        t.type === "deposit"
          ? `+${t.amount}`
          : t.type === "withdrawal"
            ? `-${Math.abs(t.amount)}`
            : t.pnl || t.amount || 0,
      Balance: t.balance || "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws["!cols"] = [
      { wch: 6 },
      { wch: 22 },
      { wch: 12 },
      { wch: 40 },
      { wch: 15 },
      { wch: 15 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Statement");

    // Add summary sheet
    const summaryData = [
      { Summary: "Total Deposits", Value: stats.totalDeposits },
      { Summary: "Total Withdrawals", Value: stats.totalWithdrawals },
      { Summary: "Trading P&L", Value: stats.totalPnl },
      { Summary: "Total Transfers", Value: stats.totalTransfers },
      { Summary: "Current Balance", Value: balance },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    wsSummary["!cols"] = [{ wch: 25 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    XLSX.writeFile(
      wb,
      `statement_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    setShowExportMenu(false);
    dispatch(
      showToast({ message: "Excel exported successfully!", type: "success" }),
    );
  };

  // Export PDF
  const exportPDF = () => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(245, 158, 11);
    doc.text("AnzoTrades", 14, 20);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text("Account Statement", 14, 28);

    doc.setFontSize(9);
    doc.text(`User: ${user?.firstName || ""} ${user?.lastName || ""}`, 14, 36);
    doc.text(`Date Range: ${fromDate} to ${toDate}`, 14, 41);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 46);

    // Summary box
    doc.setFillColor(245, 158, 11, 0.1);
    doc.rect(14, 52, 180, 22, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Deposits: ${fmtUSD(stats.totalDeposits)}`, 18, 58);
    doc.text(`Total Withdrawals: ${fmtUSD(stats.totalWithdrawals)}`, 18, 63);
    doc.text(`Trading P&L: ${fmtUSD(stats.totalPnl)}`, 95, 58);
    doc.text(`Balance: ${fmtUSD(balance)}`, 95, 63);
    doc.text(`Total Transfers: ${fmtUSD(stats.totalTransfers)}`, 150, 58);

    // Table
    const tableData = filteredTransactions.map((t) => [
      t.date || "",
      (t.type || "").toUpperCase(),
      t.description || "",
      t.type === "deposit"
        ? `+$${t.amount}`
        : t.type === "withdrawal"
          ? `-$${Math.abs(t.amount)}`
          : (t.pnl || t.amount || 0) >= 0
            ? `+$${t.pnl || t.amount || 0}`
            : `-$${Math.abs(t.pnl || t.amount || 0)}`,
      t.balance ? `$${t.balance}` : "",
    ]);

    autoTable(doc, {
      startY: 80,
      head: [["Date", "Type", "Description", "Amount", "Balance"]],
      body: tableData,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        textColor: [50, 50, 50],
      },
      headStyles: {
        fillColor: [245, 158, 11],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 20 },
        2: { cellWidth: 70 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
      },
    });

    doc.save(`statement_${new Date().toISOString().split("T")[0]}.pdf`);
    setShowExportMenu(false);
    dispatch(
      showToast({ message: "PDF exported successfully!", type: "success" }),
    );
  };

  const generateDemoTransactions = () => [
    {
      date: "2024-03-15",
      type: "deposit",
      description: "Deposit via Credit Card",
      amount: 5000,
      balance: 5000,
      pnl: null,
    },
    {
      date: "2024-03-18",
      type: "trade",
      description: "SELL BTCUSDT - Profit",
      amount: 230,
      balance: 5230,
      pnl: 230,
    },
    {
      date: "2024-04-01",
      type: "withdrawal",
      description: "Withdrawal to Bank",
      amount: -3000,
      balance: 2230,
      pnl: null,
    },
  ];

  const getTypeBadge = (type) => {
    if (type === "deposit")
      return {
        icon: "fa-circle-plus",
        text: "DEPOSIT",
        bg: "var(--color-greenBg)",
        color: "var(--color-green)",
        border: "var(--color-greenBorder)",
      };
    if (type === "withdrawal")
      return {
        icon: "fa-circle-minus",
        text: "WITHDRAWAL",
        bg: "var(--color-redBg)",
        color: "var(--color-red)",
        border: "var(--color-redBorder)",
      };
    if (type === "transfer")
      return {
        icon: "fa-arrow-right-arrow-left",
        text: "TRANSFER",
        bg: "var(--color-blueBg)",
        color: "var(--color-blue)",
        border: "var(--color-blueBorder)",
      };
    return {
      icon: "fa-chart-line",
      text: "TRADE",
      bg: "var(--color-orangeBg)",
      color: "var(--color-text)",
      border: "var(--color-orangeBorder)",
    };
  };

  const getAmountDisplay = (type, amount, pnl) => {
    if (type === "deposit")
      return (
        <span
          className="font-mono font-bold"
          style={{ color: "var(--color-green)" }}
        >
          +{fmtUSD(Math.abs(amount))}
        </span>
      );
    if (type === "withdrawal")
      return (
        <span
          className="font-mono font-bold"
          style={{ color: "var(--color-red)" }}
        >
          -{fmtUSD(Math.abs(amount))}
        </span>
      );
    if (type === "transfer")
      return (
        <span
          className="font-mono font-bold"
          style={{ color: "var(--color-blue)" }}
        >
          -{fmtUSD(Math.abs(amount))}
        </span>
      );
    const pnlValue = pnl || amount || 0;
    return (
      <span
        className="font-mono font-bold"
        style={{
          color: pnlValue >= 0 ? "var(--color-green)" : "var(--color-red)",
        }}
      >
        {pnlValue >= 0 ? "+" : ""}
        {fmtUSD(pnlValue)}
      </span>
    );
  };

  const cardStyle = {
    background: "var(--color-bgSurface)",
    border: "1px solid var(--color-border)",
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--color-bg)" }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div
          className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{
            background:
              "radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)",
          }}
        />
        <div
          className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{
            background:
              "radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)",
          }}
        />
      </div>
      <Topbar />

      <div className="relative z-10 pt-[52px] p-5 max-w-[1200px] mx-auto">
        <div className="text-center mb-8 animate-slide-up">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
            style={{
              background: "var(--color-orangeBg)",
              border: "1px solid var(--color-orangeBorder)",
            }}
          >
            <i
              className="fas fa-file-invoice-dollar text-[9px]"
              style={{ color: "var(--color-text)" }}
            />
            <span
              className="text-[9px] font-mono tracking-wide"
              style={{ color: "var(--color-text)" }}
            >
              FINANCE
            </span>
          </div>
          <h1
            className="font-syne text-3xl md:text-4xl font-extrabold mb-2"
            style={{ color: "var(--color-text)" }}
          >
            Monetary{" "}
            <span style={{ color: "var(--color-text)" }}>Statement</span>
          </h1>
          <p
            className="text-sm"
            style={{ color: "var(--color-textSecondary)" }}
          >
            View all your deposits, withdrawals, and trading activity in one
            place
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8 animate-slide-up animation-delay-100">
          {[
            {
              label: "Total Deposits",
              value: fmtUSD(stats.totalDeposits),
              color: "var(--color-green)",
              icon: "fa-circle-plus",
            },
            {
              label: "Total Withdrawals",
              value: `-${fmtUSD(stats.totalWithdrawals)}`,
              color: "var(--color-red)",
              icon: "fa-circle-minus",
            },
            {
              label: "Trading P&L",
              value: `${stats.totalPnl >= 0 ? "+" : ""}${fmtUSD(stats.totalPnl)}`,
              color:
                stats.totalPnl >= 0 ? "var(--color-green)" : "var(--color-red)",
              icon: "fa-chart-line",
            },
            {
              label: "Transfers",
              value: `-${fmtUSD(stats.totalTransfers)}`,
              color: "var(--color-blue)",
              icon: "fa-arrow-right-arrow-left",
            },
            {
              label: "Balance",
              value: fmtUSD(balance),
              color: "var(--color-purpleLight)",
              icon: "fa-wallet",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl p-4 transition-all hover:scale-[1.03]"
              style={cardStyle}
            >
              <div className="flex items-center gap-2 mb-2">
                <i
                  className={`fas ${card.icon} text-[10px]`}
                  style={{ color: card.color }}
                />
                <div
                  className="text-[8px] font-mono tracking-wide uppercase"
                  style={{ color: "var(--color-textDim)" }}
                >
                  {card.label}
                </div>
              </div>
              <div
                className="font-syne text-lg font-extrabold"
                style={{ color: card.color }}
              >
                {card.value}
              </div>
            </div>
          ))}
        </div>

        {/* Filters + Export */}
        <div
          className="rounded-xl p-4 mb-6 animate-slide-up animation-delay-200"
          style={cardStyle}
        >
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border flex-1 min-w-[150px]"
              style={{
                background: "var(--color-bgCard)",
                borderColor: "var(--color-borderMedium)",
              }}
            >
              <i
                className="fas fa-search text-[10px]"
                style={{ color: "var(--color-textDim)" }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transactions..."
                className="bg-transparent border-none text-xs outline-none flex-1"
                style={{ color: "var(--color-text)" }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="transition-colors"
                  style={{ color: "var(--color-textDim)" }}
                >
                  <i className="fas fa-times text-[10px]" />
                </button>
              )}
            </div>

            {/* Date Filters */}
            {[
              { label: "From", value: fromDate, setter: setFromDate },
              { label: "To", value: toDate, setter: setToDate },
            ].map((f) => (
              <div
                key={f.label}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border"
                style={{
                  background: "var(--color-bgCard)",
                  borderColor: "var(--color-borderMedium)",
                }}
              >
                <label
                  className="text-[8px] font-mono"
                  style={{ color: "var(--color-textDim)" }}
                >
                  {f.label}
                </label>
                <input
                  type="date"
                  value={f.value}
                  onChange={(e) => f.setter(e.target.value)}
                  className="bg-transparent border-none text-xs outline-none"
                  style={{ color: "var(--color-text)" }}
                />
              </div>
            ))}

            {/* Type Filter */}
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border"
              style={{
                background: "var(--color-bgCard)",
                borderColor: "var(--color-borderMedium)",
              }}
            >
              <label
                className="text-[8px] font-mono"
                style={{ color: "var(--color-textDim)" }}
              >
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-transparent border-none text-xs outline-none"
                style={{ color: "var(--color-text)" }}
              >
                <option style={{ color: "rgb(30 37 48)" }} value="all">All</option>
                <option style={{ color: "rgb(30 37 48)" }} value="deposit">Deposits</option>
                <option style={{ color: "rgb(30 37 48)" }} value="withdrawal">Withdrawals</option>
                <option style={{ color: "rgb(30 37 48)" }} value="trade">Trades</option>
                <option style={{ color: "rgb(30 37 48)" }} value="transfer">Transfers</option>
              </select>
            </div>

            {/* Export Dropdown */}
            <div className="relative ml-auto" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-syne text-[10px] font-bold transition-all"
                style={{
                  background: "var(--color-orangeBg)",
                  border: "1px solid var(--color-orangeBorder)",
                  color: "var(--color-text)",
                }}
              >
                <i className="fas fa-download text-[10px]" /> Export
                <i
                  className={`fas fa-chevron-down text-[8px] transition-transform ${showExportMenu ? "rotate-180" : ""}`}
                />
              </button>

              {showExportMenu && (
                <div
                  className="absolute right-0 top-full mt-1 w-44 rounded-lg shadow-2xl overflow-hidden animate-pop-in z-50"
                  style={{
                    background: "var(--color-bgSurface)",
                    border: "1px solid var(--color-border)",
                  }}
                >
                  <button
                    onClick={exportCSV}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-[11px] font-bold transition-colors"
                    style={{ color: "var(--color-textSecondary)" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--color-bgHover)";
                      e.currentTarget.style.color = "var(--color-text)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color =
                        "var(--color-textSecondary)";
                    }}
                  >
                    <i
                      className="fas fa-file-csv text-xs"
                      style={{ color: "var(--color-green)" }}
                    />{" "}
                    CSV Export
                  </button>
                  <button
                    onClick={exportExcel}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-[11px] font-bold transition-colors"
                    style={{ color: "var(--color-textSecondary)" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--color-bgHover)";
                      e.currentTarget.style.color = "var(--color-text)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color =
                        "var(--color-textSecondary)";
                    }}
                  >
                    <i
                      className="fas fa-file-excel text-xs"
                      style={{ color: "var(--color-green)" }}
                    />{" "}
                    Excel Export
                  </button>
                  <button
                    onClick={exportPDF}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-[11px] font-bold transition-colors"
                    style={{ color: "var(--color-textSecondary)" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--color-bgHover)";
                      e.currentTarget.style.color = "var(--color-text)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color =
                        "var(--color-textSecondary)";
                    }}
                  >
                    <i
                      className="fas fa-file-pdf text-xs"
                      style={{ color: "var(--color-red)" }}
                    />{" "}
                    PDF Export
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div
          className="rounded-xl overflow-hidden animate-slide-up animation-delay-300"
          style={cardStyle}
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr style={{ background: "var(--color-bgCard)" }}>
                  {[
                    "#",
                    "Date",
                    "Type",
                    "Description",
                    "Amount",
                    "Balance",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-[8px] font-mono font-bold border-b"
                      style={{
                        color: "var(--color-textDim)",
                        borderColor: "var(--color-borderMedium)",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12">
                      <div className="flex items-center justify-center">
                        <div
                          className="w-6 h-6 border-2 rounded-full animate-spin"
                          style={{
                            borderColor: "var(--color-orange)",
                            borderTopColor: "transparent",
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12">
                      <i
                        className="fas fa-inbox text-3xl mb-2 block opacity-50"
                        style={{ color: "var(--color-textDim)" }}
                      />
                      <p
                        className="font-mono text-xs"
                        style={{ color: "var(--color-textDim)" }}
                      >
                        No transactions found
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((tx, idx) => {
                    const badge = getTypeBadge(tx.type);
                    return (
                      <tr
                        key={idx}
                        className="border-b transition-all"
                        style={{ borderColor: "var(--color-borderLight)" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            "var(--color-bgHover)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td
                          className="px-4 py-3 font-mono text-[10px]"
                          style={{ color: "var(--color-textDim)" }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          className="px-4 py-3 font-mono text-[10px]"
                          style={{ color: "var(--color-textMuted)" }}
                        >
                          {tx.date}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold font-mono border"
                            style={{
                              background: badge.bg,
                              color: badge.color,
                              borderColor: badge.border,
                            }}
                          >
                            <i className={`fas ${badge.icon} text-[7px]`} />{" "}
                            {badge.text}
                          </span>
                        </td>
                        <td
                          className="px-4 py-3 font-dm text-[11px]"
                          style={{ color: "var(--color-text)" }}
                        >
                          {tx.description}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {getAmountDisplay(tx.type, tx.amount, tx.pnl)}
                        </td>
                        <td
                          className="px-4 py-3 text-right font-mono text-[11px]"
                          style={{ color: "var(--color-purpleLight)" }}
                        >
                          {tx.balance ? fmtUSD(tx.balance) : "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Toast />
      <style>{`
        @keyframes float { 0%,100% { transform:translate(0,0) } 50% { transform:translate(28px,-28px) } }
        @keyframes slide-up { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        @keyframes pop-in { from { opacity:0; transform:scale(0.93) } to { opacity:1; transform:scale(1) } }
        .animate-float { animation:float 20s ease-in-out infinite }
        .animation-delay-2000 { animation-delay:-8s }
        .animate-slide-up { animation:slide-up .6s ease-out both }
        .animation-delay-100 { animation-delay:.1s }
        .animation-delay-200 { animation-delay:.2s }
        .animation-delay-300 { animation-delay:.3s }
        .animate-pop-in { animation:pop-in 0.2s ease }
      `}</style>
    </div>
  );
};

export default Statement;
